import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { useMutation, useQuery } from '@zg-rentals/react-query5';
import {
  displaySearchPromptPostInquiry,
  getInquiryMessageCache,
  submitInquiry,
  validatePhoneAndEmail,
} from '@/src/lib/api/endpoints/inquiry';
import type {
  ContactValidationResponse,
  DisplaySearchPromptPostInquiryFilter,
  InquiryMessageCache,
  InquiryRequestBody,
} from '@/src/lib/api/endpoints/inquiry';
import { inquiryKeys } from './queryKeys';
import { USER_LISTINGS_TAG } from './queryTags';
import { useCurrentUser } from './useUserQuery';

export const useInquiryMessageCache = (options?: { enabled?: boolean }) => {
  return useQuery<InquiryMessageCache | null>({
    queryKey: inquiryKeys.messageCache(),
    queryFn: async () => {
      const response = await getInquiryMessageCache();
      return response.data ?? null;
    },
    enabled: options?.enabled,
    staleTime: 0,
  });
};

export const useDisplaySearchPromptPostInquiry = (
  filter: DisplaySearchPromptPostInquiryFilter | null,
  options?: { enabled?: boolean },
) => {
  return useQuery<boolean>({
    queryKey: inquiryKeys.displaySearchPromptPostInquiry(filter),
    queryFn: async () => {
      if (!filter) return false;
      const response = await displaySearchPromptPostInquiry(filter);
      return Boolean(response.data);
    },
    enabled: Boolean(filter) && options?.enabled !== false,
    staleTime: 0,
  });
};

export const useValidateContactInfo = () => {
  const { data: currentUser } = useCurrentUser();
  return useMutation<
    ContactValidationResponse,
    Error,
    { listingAlias?: string; email?: string; phone?: string }
  >({
    // ISAPPSEC-3939 defense-in-depth: validate the authenticated account email for
    // logged-in sessions, not a client-supplied value (see useSubmitInquiry).
    mutationFn: (params) =>
      validatePhoneAndEmail(
        currentUser?.loggedIn && currentUser.data?.email
          ? { ...params, email: currentUser.data.email }
          : params,
      ),
  });
};

export const useSubmitInquiry = () => {
  const { data: currentUser } = useCurrentUser();
  return useMutation({
    // ISAPPSEC-3939 defense-in-depth: for a logged-in session, force the inquiry email
    // to the authenticated account email so a tampered form value can't target another
    // account. Authoritative authorization fix is server-side in hotpads-api.
    mutationFn: (body: InquiryRequestBody) =>
      submitInquiry(
        currentUser?.loggedIn && currentUser.data?.email
          ? { ...body, email: currentUser.data.email }
          : body,
      ),
    // Metric names mirror legacy hotpads-web (ContactActions) so lead-submission
    // health is comparable across both apps in Marlin during the A/B test.
    onSuccess: (response) => {
      const failed = response.success === false || Boolean(response.error) || Boolean(response.cachedRequest);
      getGlobalMonitor()?.count({ name: failed ? 'ContactForm Submit Error' : 'ContactForm Submit Success' });
    },
    onError: () => {
      getGlobalMonitor()?.count({ name: 'ContactForm Submit Error' });
    },
    meta: { invalidates: [USER_LISTINGS_TAG, 'inquiry'] },
  });
};
